/**
 * STAGE 02 — BACKGROUND REMOVAL, guarded (port of bgremove.py).
 *
 * Run u2netp (via the SegmentationModel port), build a binary mask, and only
 * accept the cut when the mask is a clean, confident, convex rectangle. Use the
 * CONVEX HULL as the document region so we only ever whiten OUTSIDE it, then
 * crop tight. Otherwise keep the input unchanged with a guard reason.
 *
 * Constants match bgremove.py exactly:
 *   MIN_RECTANGULARITY=0.75, MIN_SOLIDITY=0.88, MAX_SOFT=0.13,
 *   face guard (conf>0.6, face/area>0.45), min area 0.10·frame, raw>200 px.
 *
 * If no SegmentationModel is provided, this stage is a no-op ('empty').
 */
import { ImagePixels } from '../../core/ports';
import { cloneImage, cropImage, createMask } from '../core/image';
import { convexHull, minAreaRect, polygonArea } from '../core/geometry';
import { largestBlob, fillPolygon } from '../core/contour';
import { SegmentationModel, FaceDetector } from '../ports';
import { largestFace } from './orient';

export type BgRemoveMethod = 'u2netp' | 'guard-face' | 'guard-small' | 'guard-noedge' | 'empty';

const MIN_RECTANGULARITY = 0.75;
const MIN_SOLIDITY = 0.88;
const MAX_SOFT = 0.13;

export async function removeBackground(
  img: ImagePixels,
  seg: SegmentationModel | undefined,
  faceDetector?: FaceDetector,
): Promise<{ image: ImagePixels; method: BgRemoveMethod }> {
  if (!seg) return { image: cloneImage(img), method: 'empty' };

  const H = img.height, W = img.width;
  const alpha = await seg.alpha(img); // [0,1] per pixel, image resolution
  const n = W * H;

  // raw mask = alpha > 0.5
  const raw = createMask(W, H);
  let rawSum = 0;
  for (let i = 0; i < n; i++) {
    if (alpha[i] > 0.5) {
      raw.data[i] = 255;
      rawSum++;
    }
  }
  if (rawSum < 200) return { image: cloneImage(img), method: 'empty' };

  // soft = fraction of pixels u2netp was unsure about (alpha 0.2..0.8).
  let softCount = 0;
  for (let i = 0; i < n; i++) if (alpha[i] > 0.2 && alpha[i] < 0.8) softCount++;
  const soft = softCount / n;

  const blob = largestBlob(raw);
  if (!blob) return { image: cloneImage(img), method: 'empty' };

  const area = blob.area; // ≈ cv2.contourArea (see contour.ts note)
  const hull = convexHull(blob.boundary);
  const hullArea = polygonArea(hull);
  const rect = minAreaRect(blob.boundary);
  const rw = rect.w, rh = rect.h;
  const solidity = hullArea > 0 ? area / hullArea : 0;
  const rectangularity = rw * rh > 0 ? area / (rw * rh) : 0;

  if (area < 0.1 * H * W) return { image: cloneImage(img), method: 'guard-small' };

  // Face guard: a face that fills most of the mask → mask is basically the face.
  if (faceDetector) {
    const face = await largestFace(img, faceDetector);
    if (face && face.conf > 0.6 && (face.w * face.h) / Math.max(area, 1) > 0.45) {
      return { image: cloneImage(img), method: 'guard-face' };
    }
  }

  if (soft > MAX_SOFT || solidity < MIN_SOLIDITY || rectangularity < MIN_RECTANGULARITY) {
    return { image: cloneImage(img), method: 'guard-noedge' };
  }

  // Accepted: whiten outside the convex hull, then crop to the hull bbox.
  const region = fillPolygon(hull, W, H);
  const out = cloneImage(img);
  let x0 = W, y0 = H, x1 = -1, y1 = -1;
  for (let i = 0, p = 0; i < n; i++, p += 4) {
    const a = region[i] ? 1 : 0;
    out.data[p] = out.data[p] * a + 255 * (1 - a);
    out.data[p + 1] = out.data[p + 1] * a + 255 * (1 - a);
    out.data[p + 2] = out.data[p + 2] * a + 255 * (1 - a);
    if (region[i]) {
      const x = i % W, y = (i / W) | 0;
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (y < y0) y0 = y;
      if (y > y1) y1 = y;
    }
  }
  if (x1 < x0 || y1 < y0) return { image: out, method: 'u2netp' };
  return { image: cropImage(out, x0, y0, x1, y1), method: 'u2netp' };
}
